import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Button from '@mui/material/Button'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import styled from '@emotion/styled'

import { isUndefined, orderBy } from 'lodash-es'

import { selectWebview } from 'store/webview/webviewSlice'
import {
  useVenderByUrlId,
  useAlbumCount,
  useArticleCount,
  useLoginData,
} from 'utils/api/hooks'
import { setVenderViewedOrClicked } from 'utils/api'
import { getVenderWelcomeInfo } from 'utils/helpers/vender-welcome-info'
import { getVenderJoinedYears } from 'utils/helpers/vender-joined-years'
import { getVenderViewTrackingParams } from 'utils/helpers/vender-view-page-type'
import { hasVenderAboutContent } from 'utils/helpers'
import {
  enableNativeHeaderMainTabs,
  enableNativeHeaderNavBack,
  enableNativeHeaderSearch,
} from 'utils/native-notifier'
import { useGtmVenderPageView, GtmAgent } from 'utils/hooks/analytics'
import Layout from 'components/common/Layout'
import Header from 'components/common/Header'
import Footer from 'components/common/Footer'
import ContentWrapper from 'components/common/ContentWrapper'
import InfoBlock from 'components/common/InfoBlock'
import { AlertCircleSVG, EyeSVG } from 'components/common/SVGElements'
import InfiniteContainer from 'components/common/InfiniteContainer'
import LoginRequiredModal from 'components/common/LoginRequiredModal'
import Stickable, { useCustomSticky } from 'components/common/Stickable'
import { HideInMobileBox, HideInDesktopBox } from 'components/common/Hidden'
import { InfoModal } from 'components/common/Modal'
import Link from 'components/common/A'
import Tag from 'components/common/Tag'
import NowrapDiv from 'components/common/NowrapDiv'
import ResponsiveDiv from 'components/common/ResponsiveDiv'
import ButtonBasic from 'components/common/ButtonBasic'
import VenderContact, {
  hasVenderContactContent,
  normalizeVenderContactData,
} from 'components/pages/Vender/VenderContact'
import ContactPhoneModal from 'components/pages/Vender/VenderContact/ContactPhoneModal'
import VenderRelatedLinks from 'components/pages/Vender/VenderRelatedLinks'
import { VENDER_AD_WARNING_TEXT } from 'components/pages/Vender/constants'

import VenderServiceInfoSimplified from './VenderServiceInfoSimplified'
import Company from './Company'
import DiscountInfoBlock from './DiscountInfoBlock'
import VenderBannerCarousel from './VenderBannerCarousel'
import VenderServiceIntro from './VenderServiceIntro'
import VenderServiceItems from './VenderServiceItems'
import VenderServiceFlows from './VenderServiceFlows'
import VenderWhyChooseUs from './VenderWhyChooseUs'
import VenderCertification from './VenderCertification'
import Controls from './Controls'

const DynamicAlbums = dynamic(() => import('./Albums'))
const DynamicArticles = dynamic(() => import('./Articles'))
const DynamicVenderAbout = dynamic(() =>
  import('components/pages/Vender/VenderAbout')
)

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

let pendingVenderTabScrollPosition = null

// 由於廠商代表圖於廠商頁面改版後在 mobile 需滿版，故從 ContentWrapper 抽離並客製化
const ImgWrapper = styled.div`
  position: relative;
  max-width: 1472px;
  aspect-ratio: 3;
  display: flex;
  justify-content: center;
  overflow: hidden;

  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      width: calc(100% - ${theme.spacing(18)});
      aspect-ratio: 4;
      border-radius: 4px;
    }
    ${theme.breakpoints.up('xl')} {
      width: calc(100% - ${theme.spacing(24)});
    }
    ${theme.breakpoints.up('xxl')} {
      width: calc(100% - ${theme.spacing(32)});
    }
  `}
`

const avatarSize = 100
const avatarBorderSize = 0

const AvatarRelativeWrapper = styled.div`
  position: relative;
  height: ${({ $withTypicalImage }) =>
    Math.floor($withTypicalImage ? avatarSize / 2 : avatarSize) +
    avatarBorderSize * 2}px;
  width: ${avatarSize + avatarBorderSize * 2}px;

  ${({ theme, $withTypicalImage }) => `
    ${theme.breakpoints.up('lg')} {
      height: ${
        avatarSize + avatarBorderSize * 2 - ($withTypicalImage ? 40 : 0)
      }px;
    }
  `}
`

const AvatarWrapper = styled.div`
  border-radius: 50%;
  border: ${avatarBorderSize}px solid white;
  position: absolute;
  top: 0;
  left: 0;
  background-color: white;
`
const Avatar = styled.img`
  border-radius: 50%;
  width: ${avatarSize}px;
  height: ${avatarSize}px;
  object-fit: cover;
`

const JoinYearsTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    borderRadius: '4px',
    padding: '10px',
    backgroundColor: theme.palette.text.minus1,
    color: theme.palette.white.contrastText,
    ...theme.typography.paraBodyXs,
  },
}))

const CustomTabs = styled(Tabs)`
  ${({ theme }) => `
    .MuiTabs-indicator {
      height: 4px;
    }
    .MuiTabs-indicatorSpan {
      width: 32px;
      border-radius: 4px;
      background: ${theme.palette.primary.main};
    }
    .MuiTabs-flexContainer {
      gap: 24px;
    }
  `}
`

const CustomTab = styled(Tab)`
  ${({ theme }) => `
    &.MuiTab-root {
      min-width: unset;
      padding: 14px 0;
      font-size: ${theme.typography.body1.fontSize};
      font-weight: initial;
      color: ${theme.palette.text.minus2};

      &.Mui-selected {
        font-weight: 700;
        color: ${theme.palette.primary.main};
      }
    }
  `}
`

const NameWrapper = styled.div`
  ${({ theme, $withTypicalImage }) => `
    display: flex;
    justify-content: center;
    align-items: center;

    // ${theme.breakpoints.up('lg')} {
    //   justify-content: flex-start;
    //   align-items: ${$withTypicalImage ? 'flex-start' : 'center'};
    //   margin-bottom: 0;
    //   margin-top: ${theme.spacing($withTypicalImage ? 9 : 0)};
    //   flex-grow: 1;
    // }
  `}
`

const VenderFeatureTagWrapperForMobile = styled(Box)`
  position: relative;
  right: 0;
  // 為了創造靠右效果故+16px
  width: calc(100% + 16px);
  display: flex;
  justify-content: left;
  flex-wrap: nowrap;
  gap: 8px;
  overflow: auto;

  ::-webkit-scrollbar {
    display: none;
  }
`

// 首頁資訊會有預覽需求，多一層轉換函式以確保相關區塊資料結構的正確性
const toArray = (value) => (Array.isArray(value) ? value : [])
const toObjectArray = (value) =>
  toArray(value).filter((item) => item && typeof item === 'object')

const VenderContent = ({
  isMobile,
  fallbackData,
  pageType,
  previewHomepageSetting,
  previewWelcomeInfoSetting,
  previewContactInfoSetting,
  previewCompanyIntroSetting,
  topNotice,
}) => {
  const { loggedIn } = useLoginData()
  const webview = useSelector(selectWebview)
  const theme = useTheme()
  const currentPageType = ['about', 'contact'].includes(pageType)
    ? pageType
    : 'home'
  const { gridRef, containerRef, customTabsRef, isSticky } = useCustomSticky()
  // 用寬度判斷是否為 mobile。isMobile 則以裝置類型判斷
  const isMobileByWidth = useMediaQuery(theme.breakpoints.down('lg'))
  const invokeNativeHeaderControls = () => {
    try {
      enableNativeHeaderMainTabs(true)
      enableNativeHeaderNavBack(true)
      enableNativeHeaderSearch(false)
    } catch (err) {
      console.error('native notifier error: ', err.message)
    }
  }
  useEffect(() => {
    if (webview?.name === 'lsApp') {
      invokeNativeHeaderControls()
    }
  }, [webview])

  useEffect(() => {
    // 處理列表點進廠商時，隱藏廠商沒有跳轉的問題
    if (isUndefined(fallbackData)) location.reload()
  }, [])

  const venderUrlId = fallbackData?.venderUrlId

  const restorePendingTabScrollPosition = useCallback(() => {
    const pendingPosition = pendingVenderTabScrollPosition
    const scrollContainer = containerRef.current

    if (
      !pendingPosition ||
      pendingPosition.venderUrlId !== venderUrlId ||
      pendingPosition.pageType !== currentPageType
    ) {
      return
    }

    const { scrollTop } = pendingPosition
    const applyScrollPosition = (container) => {
      if (!container || pendingVenderTabScrollPosition !== pendingPosition) {
        return
      }

      pendingVenderTabScrollPosition = null
      container.scrollTop = scrollTop

      window.requestAnimationFrame(() => {
        if (containerRef.current === container) {
          container.scrollTop = scrollTop
        }
      })
    }

    if (scrollContainer) {
      applyScrollPosition(scrollContainer)
      return
    }

    window.requestAnimationFrame(() => {
      applyScrollPosition(containerRef.current)
    })
  }, [containerRef, currentPageType, venderUrlId])

  useIsomorphicLayoutEffect(() => {
    if (currentPageType !== 'about') {
      restorePendingTabScrollPosition()
    }
  }, [currentPageType, restorePendingTabScrollPosition])

  const { data: vender } = useVenderByUrlId(venderUrlId, {
    fallbackData: fallbackData?.vender,
  })
  const venderWithWelcomePreview = useMemo(() => {
    if (isUndefined(previewWelcomeInfoSetting) || !vender) return vender

    return {
      ...vender,
      welcomeInfoSetting: previewWelcomeInfoSetting,
    }
  }, [previewWelcomeInfoSetting, vender])

  useEffect(() => {
    if (vender?._id) {
      setVenderViewedOrClicked(
        vender._id,
        getVenderViewTrackingParams(currentPageType)
      )
    }
  }, [currentPageType, vender?._id])

  const { data: albumCount } = useAlbumCount(
    () => (vender?._id ? { venderId: vender?._id } : null),
    { fallbackData: fallbackData?.albumCount }
  )
  const { data: articleCount } = useArticleCount(
    () => (vender?._id ? { venderId: vender?._id } : null),
    { fallbackData: fallbackData?.articleCount }
  )

  const [isContactNumberModalOpen, setContactNumberModalOpen] = useState(false)
  const [isLoginModalOpen, setLoginModalOpen] = useState(false)
  const [isJoinYearsModalOpen, setJoinYearsModalOpen] = useState(false)
  const [isDiscountExpanded, setDiscountExpanded] = useState(false)
  const openContactNumberModal = () => {
    if (loggedIn) {
      setContactNumberModalOpen(true)
    } else {
      setLoginModalOpen(true)
    }
  }
  const onContactNumberBtnClick = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_contact',
      living_click_name: '聯絡電話',
      living_click_location: '廠商頁',
      living_vender: vender?.name,
      living_vender_id: vender?._id,
    })
    openContactNumberModal()
  }
  const onContactNumberModalClose = () => setContactNumberModalOpen(false)
  const onLoginModalClose = () => setLoginModalOpen(false)
  const onDialNumberClick = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_contact',
      living_click_name: '電話號碼',
      living_click_location: '光箱頁-廠商連絡電話',
      living_vender: vender?.name,
      living_vender_id: vender?._id,
    })
  }
  const onDialButtonClick = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_contact',
      living_click_name: '撥打電話',
      living_click_location: '光箱頁-廠商連絡電話',
      living_vender: vender?.name,
      living_vender_id: vender?._id,
    })
  }
  const handleLogin = () => {
    const redirectUrl = window.location.href
    window.location.href = `/login?rt=${encodeURIComponent(redirectUrl)}`
  }
  const [linkConfirmation, setLinkConfirmation] = useState({
    isOpen: false,
    url: '',
    name: '',
  })

  const isLivingLifeDomain = (url) => {
    // 避免資料型態造成後續流程中斷
    if (!url || typeof url !== 'string') return true
    // 檢查是否為相對路徑或錨點連結
    if (url.startsWith('//') || url.startsWith('/') || url.startsWith('#')) {
      return true
    }
    try {
      const hostname = new URL(url).hostname
      return (
        hostname.endsWith('.livinglife.com.tw') ||
        hostname === 'livinglife.com.tw'
      )
    } catch (e) {
      // 避免後續流程中斷
      return true
    }
  }

  const handleExternalLinkClick = (url, name) => {
    if (isLivingLifeDomain(url)) {
      window.open(url, '_blank')
    } else {
      setLinkConfirmation({ isOpen: true, url, name })
    }
  }

  // 自動關閉不送 GA
  const closeLinkConfirmation = () => {
    setLinkConfirmation({ isOpen: false, url: '', name: '' })
  }

  // 主動關閉則送 GA
  const handleCloseLinkConfirmation = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_non_ls_link_alert',
      living_vender: vender?.name,
      living_vender_id: vender?._id,
      living_external_link_name: linkConfirmation.name,
      living_click_name: '關閉',
      living_click_location: '廠商頁彈窗-前往連結',
    })
    closeLinkConfirmation()
  }

  const proceedWithExternalLink = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_non_ls_link_alert',
      living_vender: vender?.name,
      living_vender_id: vender?._id,
      living_external_link_name: linkConfirmation.name,
      living_click_name: '前往',
      living_click_location: '廠商頁彈窗-前往連結',
    })
    window.open(linkConfirmation.url, '_blank', 'noopener,noreferrer')
    closeLinkConfirmation()
  }

  useGtmVenderPageView(vender)

  const containerId = 'vender-intro-container'

  const {
    logoImage,
    typicalImage,
    typicalMobileImage,
    announcementText,
    favorableDiscountText,
  } = getVenderWelcomeInfo(venderWithWelcomePreview)
  const hasTypicalImage = !!typicalImage?.url
  const hasTypicalMobileImage = !!typicalMobileImage?.url
  const hasLogo = !!logoImage?.url
  const visibleVenderFeatureTag = (
    vender?.ownQualityTag?.qualityTags || []
  ).filter((tag) => tag.isVisible)
  const hasVisibleVenderFeatureTag = visibleVenderFeatureTag.length > 0
  const sortedVisibleVenderFeatureTag = orderBy(
    visibleVenderFeatureTag,
    ['sort', 'createdAt'],
    ['desc', 'desc']
  )
  const isGoToReservationBtnShown = !!vender?.isDisplayGotoReservation
  const isAlbumMoreThanDisplayNum = albumCount > (isMobileByWidth ? 6 : 8)
  const isArticleMoreThanDisplayNum = articleCount > 4

  const homepageData = isUndefined(previewHomepageSetting)
    ? vender?.homepageSetting
    : previewHomepageSetting

  const venderBanners = toObjectArray(homepageData?.banners)
  const venderServiceIntro = homepageData?.serviceIntroduction?.introduction
    ? {
        description: homepageData.serviceIntroduction.introduction,
        image: homepageData.serviceIntroduction.image,
      }
    : null
  const venderServiceItems = toObjectArray(homepageData?.serviceItems).map(
    (item) => ({
      ...item,
      tag: item?.tagWord,
    })
  )
  const venderServiceFlows = toObjectArray(homepageData?.serviceFlow)
  const venderWhyChooseUs = homepageData?.chooseUsReasons
    ? {
        reasons: toObjectArray(
          homepageData.chooseUsReasons.chooseUsReasonsContents
        ),
        image: homepageData.chooseUsReasons.image,
      }
    : null
  const venderSinyiLivingCertification =
    vender?.introductionInfo?.sinyiLivingCertification
  const isAboutPage = currentPageType === 'about'
  const venderWithAboutPreview = useMemo(() => {
    if (isUndefined(previewCompanyIntroSetting) || !vender) return vender

    return {
      ...vender,
      companyIntroSetting: previewCompanyIntroSetting,
    }
  }, [previewCompanyIntroSetting, vender])
  const resolvedAboutData = venderWithAboutPreview?.companyIntroSetting
  const hasAbout = hasVenderAboutContent(resolvedAboutData)
  const venderWithContactPreview = useMemo(() => {
    if (isUndefined(previewContactInfoSetting) || !vender) return vender

    return {
      ...vender,
      contactInfoSetting: previewContactInfoSetting,
    }
  }, [previewContactInfoSetting, vender])
  const resolvedContactData = useMemo(
    () => normalizeVenderContactData({ vender: venderWithContactPreview }),
    [venderWithContactPreview]
  )
  const hasContact = hasVenderContactContent(resolvedContactData)
  const isContactPage = currentPageType === 'contact'

  const joinYears = getVenderJoinedYears(
    vender?.introductionInfo?.joinedSinyiLivingDate
  )

  const pageViews = vender?.introductionInfo?.joinedSinyiLivingDate
    ? vender?.viewPage?.all ?? 0
    : 0

  // 判斷是否使用新版版面：homepageSetting 中任一區塊有資料即啟用
  const hasNewLayout = !!(
    venderBanners.length ||
    venderServiceIntro ||
    venderServiceItems.length ||
    venderServiceFlows.length ||
    venderWhyChooseUs?.reasons?.length
  )
  const tabClickLocation = {
    about: '廠商公司介紹頁',
    contact: '廠商聯繫資訊頁',
    home: '廠商首頁',
  }[currentPageType]

  const sendTabGtmEvent = (clickName) => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_tab',
      living_click_name: clickName,
      living_click_location: tabClickLocation,
      living_vender: vender?.name,
      living_vender_id: vender?._id,
    })
  }

  const onVenderTabClick = (event, clickName, destinationPageType) => {
    const isNormalNavigation =
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey

    if (
      isNormalNavigation &&
      destinationPageType !== currentPageType &&
      containerRef.current
    ) {
      pendingVenderTabScrollPosition = {
        venderUrlId,
        pageType: destinationPageType,
        scrollTop: containerRef.current.scrollTop,
      }
    }

    sendTabGtmEvent(clickName)
  }

  const onShowAllAlbumsBtnClick = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_photoalbum',
      living_click_name: `顯示全部${albumCount}本相簿`,
      living_click_location: '廠商頁',
      living_vender: vender?.name,
    })
    const newUrl = `${window.location.origin}/album?venderId=${vender._id}`
    window.open(newUrl, '_blank', 'noopener,noreferrer')
  }
  const onShowAllArticlesBtnClick = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_article',
      living_click_name: `顯示全部${articleCount}篇文章`,
      living_click_location: '廠商頁',
      living_vender: vender?.name,
    })
    const newUrl = `${window.location.origin}/article/vender-id/${vender._id}`
    window.open(newUrl, '_blank', 'noopener,noreferrer')
  }

  const contactPhone = resolvedContactData.phone
  const contactExtension = resolvedContactData.extension

  return (
    <Layout
      stickyHeader={<Header />}
      // 手機版的前往預約按鈕
      stickyFooter={
        <HideInDesktopBox bgcolor="white" p={5}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap="10px"
          >
            {/* 聯絡電話 */}
            {!!contactPhone && (
              <Button
                id="contact"
                className="action"
                variant="outlined"
                color="primary"
                // 目前無法讓兩個按鍵均分寬度，所以先設定 93% 寬度，之後可能要從 Controls 元件去調整
                sx={{ width: '93%', height: '54px', fontSize: '18px' }}
                onClick={onContactNumberBtnClick}
              >
                聯絡電話
              </Button>
            )}
            {/* 透過信義居家預約 */}
            {isGoToReservationBtnShown && (
              <Controls
                vender={vender}
                isMobileByWidth={isMobileByWidth}
                onExternalLinkClick={handleExternalLinkClick}
              />
            )}
          </Box>
        </HideInDesktopBox>
      }
      fixContentHeight={true}
    >
      {topNotice}
      <InfiniteContainer id={containerId} containerRef={containerRef}>
        <InfoModal
          open={linkConfirmation.isOpen}
          onClose={closeLinkConfirmation}
        >
          <Typography variant="titleLarge">
            <Box p={5}>前往連結</Box>
          </Typography>
          <Box p={5}>{VENDER_AD_WARNING_TEXT}</Box>
          <Box display="flex" justifyContent="flex-end" p={5}>
            <Box mr={2.5}>
              <ButtonBasic
                variant="outlined"
                size="medium"
                color="neutral"
                onClick={handleCloseLinkConfirmation}
              >
                關閉
              </ButtonBasic>
            </Box>
            <ButtonBasic
              variant="contained"
              size="medium"
              color="primary"
              onClick={proceedWithExternalLink}
            >
              前往
            </ButtonBasic>
          </Box>
        </InfoModal>
        <ContactPhoneModal
          open={isContactNumberModalOpen}
          onClose={onContactNumberModalClose}
          phone={contactPhone}
          extension={contactExtension}
          shortname={vender?.shortname}
          onPhoneClick={onDialNumberClick}
          onCallClick={onDialButtonClick}
          phoneLinkId="dial_number"
          callButtonId="dial_btn"
        />
        <LoginRequiredModal
          open={isLoginModalOpen}
          onClose={onLoginModalClose}
          onLogin={handleLogin}
        />
        {/* 廠商代表圖 */}
        {/* desktop 代表圖 */}
        {!isMobileByWidth && hasTypicalImage && (
          <Box lineHeight={1} display="flex" justifyContent="center">
            <ImgWrapper>
              <Image
                src={typicalImage.url}
                style={{ objectFit: 'cover' }}
                fill
                priority
                alt=""
                quality={60}
              />
            </ImgWrapper>
          </Box>
        )}
        {/* mobile 代表圖 */}
        {isMobileByWidth && hasTypicalMobileImage && (
          <Box lineHeight={1}>
            <ImgWrapper>
              <Image
                src={typicalMobileImage.url}
                style={{ objectFit: 'cover' }}
                fill
                priority
                alt=""
                quality={60}
              />
            </ImgWrapper>
          </Box>
        )}

        {/* 主要內容 */}
        <ContentWrapper>
          {/* 主要資訊區 */}
          <Box ref={gridRef}>
            {/* avatar/廠商名/按鍵區 */}
            <Box>
              <Box
                pt={8}
                pb={4}
                height={avatarSize + avatarBorderSize * 2 + 48}
                display="flex"
                justifyContent="space-between"
                bgcolor="background.default"
              >
                {/* 左側 */}
                <Box display="flex" pr={isMobileByWidth ? 0 : 5}>
                  {/*  logo 頭像 */}
                  {hasLogo && (
                    <Box display="flex" justifyContent="center" mr={5}>
                      <AvatarRelativeWrapper
                        $withTypicalImage={hasTypicalImage}
                      >
                        <AvatarWrapper>
                          <Avatar src={logoImage.url} />
                        </AvatarWrapper>
                      </AvatarRelativeWrapper>
                    </Box>
                  )}
                  {/* 廠商名稱 */}
                  <NameWrapper $withTypicalImage={hasTypicalImage}>
                    <Box>
                      <Typography variant="headlineMedium">
                        <Box color="text.minus2">{vender?.shortname}</Box>
                      </Typography>
                    </Box>
                  </NameWrapper>
                </Box>
                {/* 右側按鍵區 */}
                <HideInMobileBox>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap="20px"
                    height="100%"
                  >
                    {/* 聯絡電話 */}
                    {!!contactPhone && (
                      <Button
                        id="contact"
                        className="action"
                        variant="outlined"
                        color="primary"
                        sx={{
                          fontSize: '18px',
                          width: '180px',
                          height: '54px',
                        }}
                        onClick={onContactNumberBtnClick}
                      >
                        聯絡電話
                      </Button>
                    )}
                    {/* 透過信義居家預約 */}
                    {isGoToReservationBtnShown && (
                      <Box width="180px">
                        <Controls
                          vender={vender}
                          onExternalLinkClick={handleExternalLinkClick}
                        />
                      </Box>
                    )}
                  </Box>
                </HideInMobileBox>
              </Box>
            </Box>

            {/* 加入信義居家時長 / 瀏覽數量 */}
            <Box display="flex" alignItems="center" mb={4}>
              {/* 加入信義居家時長 */}
              {joinYears >= 1 && (
                <Box p={2}>
                  {isMobile ? (
                    <>
                      <Typography
                        variant="labelLarge"
                        color="text.minus1"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setJoinYearsModalOpen(true)}
                      >
                        {joinYears}年
                      </Typography>
                      <InfoModal
                        open={isJoinYearsModalOpen}
                        onClose={() => setJoinYearsModalOpen(false)}
                      >
                        <Typography variant="paraBodyMedium">
                          <Box p={5} textAlign="center">
                            {vender?.shortname} 已加入信義居家 {joinYears} 年
                          </Box>
                        </Typography>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          px={5}
                          pb={5}
                        >
                          <ButtonBasic
                            variant="outlined"
                            onClick={() => setJoinYearsModalOpen(false)}
                            size="medium"
                            color="neutral"
                            sx={{ borderColor: 'text.minus1' }}
                          >
                            關閉
                          </ButtonBasic>
                        </Box>
                      </InfoModal>
                    </>
                  ) : (
                    <JoinYearsTooltip
                      title={`${vender?.shortname} 已加入信義居家 ${joinYears} 年`}
                      followCursor
                    >
                      <Typography variant="labelLarge" color="text.minus1">
                        {joinYears}年
                      </Typography>
                    </JoinYearsTooltip>
                  )}
                </Box>
              )}
              {/* 右側：網頁瀏覽數量 + badge */}
              <Box display="flex" alignItems="center" p={2} gap={4}>
                {/* 網頁瀏覽數量 */}
                <Box display="flex" alignItems="center" gap={1}>
                  <EyeSVG $color="text.minus1" />
                  <Typography variant="labelLarge" color="text.minus1">
                    {pageViews.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* 特色標籤區（最多顯示 30 個） */}
            <Box>
              {hasVisibleVenderFeatureTag &&
                (isMobile ? (
                  // mobile 版
                  <VenderFeatureTagWrapperForMobile>
                    {sortedVisibleVenderFeatureTag.slice(0, 30).map(
                      (featureTag, index) =>
                        featureTag.isVisible && (
                          <Box display="inline-block" key={index}>
                            <Tag size="sm">
                              <NowrapDiv>{featureTag.name}</NowrapDiv>
                            </Tag>
                          </Box>
                        )
                    )}
                  </VenderFeatureTagWrapperForMobile>
                ) : (
                  // desktop 版
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    {sortedVisibleVenderFeatureTag.slice(0, 30).map(
                      (featureTag, index) =>
                        featureTag.isVisible && (
                          <Box display="inline-block" key={index}>
                            <Tag size="sm">{featureTag.name}</Tag>
                          </Box>
                        )
                    )}
                  </Box>
                ))}
            </Box>

            {/* 詳細資訊與特色優惠區 */}
            <Box my={5} display="flex" columnGap={{ lg: 6 }}>
              {/* 廠商詳細資訊/查看更多資訊 */}
              <Box
                style={{
                  flexGrow: 3,
                  flexBasis: '0%',
                  minWidth: 0,
                }}
              >
                <Box>
                  {/* 服務項目/服務區域 */}
                  <Box>
                    <VenderServiceInfoSimplified vender={vender} />
                  </Box>
                  {!!resolvedContactData.links.length && (
                    <VenderRelatedLinks
                      links={resolvedContactData.links}
                      vender={vender}
                      clickLocation="下方連結"
                      onExternalLinkClick={handleExternalLinkClick}
                      columnGap={4}
                      rowGap={2}
                    />
                  )}
                </Box>
              </Box>
              {/* 特色優惠 for desktop */}
              {!!favorableDiscountText && (
                <HideInMobileBox
                  sx={{ flexGrow: 2, flexBasis: '0%', minWidth: 0 }}
                >
                  <Box width="100%">
                    <DiscountInfoBlock
                      vender={venderWithWelcomePreview}
                      expanded={isDiscountExpanded}
                      onExpandedChange={setDiscountExpanded}
                      $borderColor="primary.minus2"
                    />
                  </Box>
                </HideInMobileBox>
              )}
            </Box>

            {/* 特色優惠 for mobile */}
            <HideInDesktopBox>
              {!!favorableDiscountText && (
                <Box mb={5}>
                  <DiscountInfoBlock
                    vender={venderWithWelcomePreview}
                    expanded={isDiscountExpanded}
                    onExpandedChange={setDiscountExpanded}
                    $borderColor="primary.minus2"
                  />
                </Box>
              )}
            </HideInDesktopBox>

            {/* 公告內容區 */}
            {!!announcementText && (
              <Box mb={8}>
                <InfoBlock
                  px={5}
                  py={6}
                  display="flex"
                  alignItems="center"
                  $bgColor="grey.third"
                  $borderColor="grey.secondary"
                >
                  <Box>
                    <AlertCircleSVG
                      $color="text.minus1"
                      style={{
                        verticalAlign: 'middle',
                        display: 'block',
                      }}
                    />
                  </Box>
                  <Typography
                    style={{
                      verticalAlign: 'middle',
                      marginLeft: '8px',
                      lineHeight: '24px',
                      fontSize: '14px',
                    }}
                  >
                    {announcementText}
                  </Typography>
                </InfoBlock>
              </Box>
            )}
          </Box>

          {/* tab 切換區 */}
          <Stickable
            $isSticky={isSticky}
            $isHideScrollbar={true}
            $hasHeader={true}
            $bgColor={theme.palette.background.default}
            $zIndex={10}
            $mobileFullWidth={true}
            style={{ marginBottom: '24px' }}
          >
            <CustomTabs
              ref={customTabsRef}
              value={currentPageType}
              variant="scrollable"
              indicatorColor="primary"
              textColor="primary"
              aria-label="廠商頁面選單"
              TabIndicatorProps={{
                children: <span className="MuiTabs-indicatorSpan" />,
                sx: {
                  display: 'flex',
                  justifyContent: 'center',
                  background: 'transparent',
                },
              }}
            >
              <CustomTab
                value="home"
                label="首頁"
                component={Link}
                href={`/vender/${vender?.urlId}`}
                scroll={false}
                onClick={(event) => onVenderTabClick(event, '首頁', 'home')}
              />
              {!!articleCount && (
                <CustomTab
                  value="article"
                  label="文章"
                  component="a"
                  href={`/article/vender-id/${vender?._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => sendTabGtmEvent('文章')}
                />
              )}
              {!!albumCount && (
                <CustomTab
                  value="album"
                  label="相簿"
                  component="a"
                  href={`/album?venderId=${vender?._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => sendTabGtmEvent('相簿')}
                />
              )}
              {hasAbout && (
                <CustomTab
                  value="about"
                  label="公司介紹"
                  component={Link}
                  href={`/vender/${vender?.urlId}/about`}
                  scroll={false}
                  onClick={(event) =>
                    onVenderTabClick(event, '公司介紹', 'about')
                  }
                />
              )}
              {hasContact && (
                <CustomTab
                  value="contact"
                  label="聯繫資訊"
                  component={Link}
                  href={`/vender/${vender?.urlId}/contact`}
                  scroll={false}
                  onClick={(event) =>
                    onVenderTabClick(event, '聯繫資訊', 'contact')
                  }
                />
              )}
            </CustomTabs>
          </Stickable>

          {/* 內容區 */}
          {isContactPage ? (
            <VenderContact
              data={resolvedContactData}
              vender={vender}
              loggedIn={loggedIn}
              onPhoneDetailClick={openContactNumberModal}
              onLoginRequired={() => setLoginModalOpen(true)}
              onExternalLinkClick={handleExternalLinkClick}
            />
          ) : isAboutPage ? (
            <DynamicVenderAbout
              data={resolvedAboutData}
              venderName={vender?.name}
              venderId={vender?._id}
              onContentReady={restorePendingTabScrollPosition}
            />
          ) : (
            <>
              <Box>
                {hasNewLayout ? (
                  <>
                    {/* 新版版面：Banner + 服務介紹 + 服務項目 + 服務流程 */}
                    {!!venderBanners?.length && (
                      <VenderBannerCarousel
                        banners={venderBanners}
                        isMobile={isMobile}
                        venderName={vender?.name}
                        venderId={vender?._id}
                      />
                    )}
                    <VenderServiceIntro serviceIntro={venderServiceIntro} />
                    <VenderServiceItems
                      serviceItems={venderServiceItems}
                      venderName={vender?.name}
                      venderId={vender?._id}
                    />
                    <VenderServiceFlows serviceFlows={venderServiceFlows} />
                  </>
                ) : (
                  /* 舊版版面：廠商介紹模組 */
                  <Company vender={vender} containerId={containerId} />
                )}

                {/* 相簿圖輯 */}
                {!!albumCount && (
                  <Box mb={18}>
                    <ResponsiveDiv>
                      <Typography pb={6} variant="h5">
                        相簿圖輯
                      </Typography>
                    </ResponsiveDiv>
                    <DynamicAlbums
                      venderId={vender?._id}
                      containerId={containerId}
                      isMobile={isMobile}
                      count={albumCount}
                      nativeHeaderControlHandler={invokeNativeHeaderControls}
                    />
                    {/* 顯示全部相簿按鈕 */}
                    {isAlbumMoreThanDisplayNum && (
                      <Button
                        id="show_all_albums"
                        className="action"
                        variant="outlined"
                        size="small"
                        color="primary"
                        sx={{
                          marginTop: '24px',
                          color: '#383838',
                          borderColor: '#383838',
                          fontWeight: 400,
                        }}
                        onClick={onShowAllAlbumsBtnClick}
                      >
                        顯示全部 {albumCount} 本相簿
                      </Button>
                    )}
                  </Box>
                )}

                {/* 文章撰寫 */}
                {!!articleCount && (
                  <Box mb={18}>
                    <ResponsiveDiv>
                      <Typography pb={6} variant="h5">
                        文章撰寫
                      </Typography>
                    </ResponsiveDiv>
                    <DynamicArticles
                      venderId={vender?._id}
                      containerId={containerId}
                      venderName={vender?.name}
                    />
                    {/* 顯示全部文章按鈕 */}
                    {isArticleMoreThanDisplayNum && (
                      <Button
                        id="show_all_articles"
                        className="action"
                        variant="outlined"
                        size="small"
                        color="primary"
                        sx={{
                          marginTop: '24px',
                          color: '#383838',
                          borderColor: '#383838',
                          fontWeight: 400,
                        }}
                        onClick={onShowAllArticlesBtnClick}
                      >
                        顯示全部 {articleCount} 篇文章
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* 選擇我們的理由 */}
              {hasNewLayout && (
                <VenderWhyChooseUs
                  reasons={venderWhyChooseUs?.reasons}
                  image={venderWhyChooseUs?.image}
                />
              )}

              {/* 信義居家認證合作廠商 + 歷年認證標章 */}
              <VenderCertification
                sinyiLivingCertification={venderSinyiLivingCertification}
              />
            </>
          )}

          {/* 底部警示文案 */}
          <Box mb={8}>
            <Typography variant="paraBodySmall" color="text.secondary">
              *{VENDER_AD_WARNING_TEXT}
            </Typography>
          </Box>
        </ContentWrapper>

        {/* Footer */}
        {webview?.name !== 'lsApp' && (
          <HideInMobileBox>
            <Footer />
          </HideInMobileBox>
        )}
      </InfiniteContainer>
    </Layout>
  )
}

VenderContent.defaultProps = {
  isMobile: true,
  pageType: 'home',
}

export default VenderContent
